/**
 * @module useBooking
 */
import { useCallback } from 'react'
import {
  useGetBookingsMutation,
  useGetBookingMutation,
  useCreateBookingMutation,
  useUpdateBookingMutation,
  useDeleteBookingMutation,
  useConfirmBookingMutation,
  useCancelBookingMutation,
  useProcessPaymentMutation,
  useUpdatePaymentStatusMutation,
  useUpdateTransactionStatusMutation,
  useGetBookingsByPassengerMutation,
  useGetBookingsByFlightMutation,
  useGetActiveBookingsMutation,
} from '../services/booking.service'

export const useBooking = () => {
  const [executeGetBookings, { isLoading: isGetBookingsLoading }] = useGetBookingsMutation()
  const [executeGetBooking, { isLoading: isGetBookingLoading }] = useGetBookingMutation()
  const [executeCreateBooking, { isLoading: isCreateBookingLoading }] = useCreateBookingMutation()
  const [executeUpdateBooking, { isLoading: isUpdateBookingLoading }] = useUpdateBookingMutation()
  const [executeDeleteBooking, { isLoading: isDeleteBookingLoading }] = useDeleteBookingMutation()
  const [executeConfirmBooking, { isLoading: isConfirmBookingLoading }] =
    useConfirmBookingMutation()
  const [executeCancelBooking, { isLoading: isCancelBookingLoading }] = useCancelBookingMutation()
  const [executeProcessPayment, { isLoading: isProcessPaymentLoading }] =
    useProcessPaymentMutation()
  const [executeUpdatePaymentStatus, { isLoading: isUpdatePaymentStatusLoading }] =
    useUpdatePaymentStatusMutation()
  const [executeUpdateTransactionStatus, { isLoading: isUpdateTransactionStatusLoading }] =
    useUpdateTransactionStatusMutation()
  const [executeGetBookingsByPassenger, { isLoading: isGetBookingsByPassengerLoading }] =
    useGetBookingsByPassengerMutation()
  const [executeGetBookingsByFlight, { isLoading: isGetBookingsByFlightLoading }] =
    useGetBookingsByFlightMutation()
  const [executeGetActiveBookings, { isLoading: isGetActiveBookingsLoading }] =
    useGetActiveBookingsMutation()

  const getBookingsAsync = useCallback(
    async (params = {}) => {
      if (isGetBookingsLoading) {
        return
      }
      try {
        const response = await executeGetBookings(params).unwrap()
        return response.data.bookings
      } catch (error) {
        console.error('#getBookingsAsync: Error fetching bookings', error)
        throw error
      }
    },
    [executeGetBookings, isGetBookingsLoading],
  )

  const getBookingAsync = useCallback(
    async (bookingId) => {
      if (isGetBookingLoading) {
        return
      }
      try {
        const response = await executeGetBooking(bookingId).unwrap()
        return response.data.booking
      } catch (error) {
        console.error('#getBookingAsync: Error fetching booking', error)
        throw error
      }
    },
    [executeGetBooking, isGetBookingLoading],
  )

  const createBookingAsync = useCallback(
    async (bookingData) => {
      if (isCreateBookingLoading) {
        return
      }
      try {
        const response = await executeCreateBooking(bookingData).unwrap()
        return response.data.booking
      } catch (error) {
        console.error('#createBookingAsync: Error creating booking', error)
        throw error
      }
    },
    [executeCreateBooking, isCreateBookingLoading],
  )

  const updateBookingAsync = useCallback(
    async (bookingId, updates) => {
      if (isUpdateBookingLoading) {
        return
      }
      try {
        const response = await executeUpdateBooking({ bookingId, ...updates }).unwrap()
        return response.data.booking
      } catch (error) {
        console.error('#updateBookingAsync: Error updating booking', error)
        throw error
      }
    },
    [executeUpdateBooking, isUpdateBookingLoading],
  )

  const deleteBookingAsync = useCallback(
    async (bookingId) => {
      if (isDeleteBookingLoading) {
        return
      }
      try {
        const response = await executeDeleteBooking(bookingId).unwrap()
        return response.data
      } catch (error) {
        console.error('#deleteBookingAsync: Error deleting booking', error)
        throw error
      }
    },
    [executeDeleteBooking, isDeleteBookingLoading],
  )

  const confirmBookingAsync = useCallback(
    async (bookingId) => {
      if (isConfirmBookingLoading) {
        return
      }
      try {
        const response = await executeConfirmBooking(bookingId).unwrap()
        return response.data.booking
      } catch (error) {
        console.error('#confirmBookingAsync: Error confirming booking', error)
        throw error
      }
    },
    [executeConfirmBooking, isConfirmBookingLoading],
  )

  const cancelBookingAsync = useCallback(
    async (bookingId) => {
      if (isCancelBookingLoading) {
        return
      }
      try {
        const response = await executeCancelBooking(bookingId).unwrap()
        return response.data.booking
      } catch (error) {
        console.error('#cancelBookingAsync: Error canceling booking', error)
        throw error
      }
    },
    [executeCancelBooking, isCancelBookingLoading],
  )

  const processPaymentAsync = useCallback(
    async (bookingId, paymentData) => {
      if (isProcessPaymentLoading) {
        return
      }
      try {
        const response = await executeProcessPayment({ bookingId, ...paymentData }).unwrap()
        return response.data.booking
      } catch (error) {
        console.error('#processPaymentAsync: Error processing payment', error)
        throw error
      }
    },
    [executeProcessPayment, isProcessPaymentLoading],
  )

  const updatePaymentStatusAsync = useCallback(
    async (bookingId, paymentStatus) => {
      if (isUpdatePaymentStatusLoading) {
        return
      }
      try {
        const response = await executeUpdatePaymentStatus({ bookingId, paymentStatus }).unwrap()
        return response.data.booking
      } catch (error) {
        console.error('#updatePaymentStatusAsync: Error updating payment status', error)
        throw error
      }
    },
    [executeUpdatePaymentStatus, isUpdatePaymentStatusLoading],
  )

  const updateTransactionStatusAsync = useCallback(
    async (bookingId, transactionStatus) => {
      if (isUpdateTransactionStatusLoading) {
        return
      }
      try {
        const response = await executeUpdateTransactionStatus({
          bookingId,
          transactionStatus,
        }).unwrap()
        return response.data.booking
      } catch (error) {
        console.error('#updateTransactionStatusAsync: Error updating transaction status', error)
        throw error
      }
    },
    [executeUpdateTransactionStatus, isUpdateTransactionStatusLoading],
  )

  const getBookingsByPassengerAsync = useCallback(
    async (passengerId, params = {}) => {
      if (isGetBookingsByPassengerLoading) {
        return
      }
      try {
        const response = await executeGetBookingsByPassenger({ passengerId, ...params }).unwrap()
        return response.data.bookings
      } catch (error) {
        console.error('#getBookingsByPassengerAsync: Error fetching bookings by passenger', error)
        throw error
      }
    },
    [executeGetBookingsByPassenger, isGetBookingsByPassengerLoading],
  )

  const getBookingsByFlightAsync = useCallback(
    async (flightId, params = {}) => {
      if (isGetBookingsByFlightLoading) {
        return
      }
      try {
        const response = await executeGetBookingsByFlight({ flightId, ...params }).unwrap()
        return response.data.bookings
      } catch (error) {
        console.error('#getBookingsByFlightAsync: Error fetching bookings by flight', error)
        throw error
      }
    },
    [executeGetBookingsByFlight, isGetBookingsByFlightLoading],
  )

  const getActiveBookingsAsync = useCallback(
    async (params = {}) => {
      if (isGetActiveBookingsLoading) {
        return
      }
      try {
        const response = await executeGetActiveBookings(params).unwrap()
        return response.data.bookings
      } catch (error) {
        console.error('#getActiveBookingsAsync: Error fetching active bookings', error)
        throw error
      }
    },
    [executeGetActiveBookings, isGetActiveBookingsLoading],
  )

  return {
    getBookingsAsync,
    getBookingAsync,
    createBookingAsync,
    updateBookingAsync,
    deleteBookingAsync,
    confirmBookingAsync,
    cancelBookingAsync,
    processPaymentAsync,
    updatePaymentStatusAsync,
    updateTransactionStatusAsync,
    getBookingsByPassengerAsync,
    getBookingsByFlightAsync,
    getActiveBookingsAsync,
    isGetBookingsLoading,
    isGetBookingLoading,
    isCreateBookingLoading,
    isUpdateBookingLoading,
    isDeleteBookingLoading,
    isConfirmBookingLoading,
    isCancelBookingLoading,
    isProcessPaymentLoading,
    isUpdatePaymentStatusLoading,
    isUpdateTransactionStatusLoading,
    isGetBookingsByPassengerLoading,
    isGetBookingsByFlightLoading,
    isGetActiveBookingsLoading,
  }
}
